import { useCallback, useLayoutEffect, useRef } from 'react';
import { ExpandLessIcon, ExpandMoreIcon } from '@/components/ui/Icons';
import { formatSecondsToHMS } from '@/utils/readyMonthStats';

export interface ReadyMonthInProgressSummary {
  totalSeconds: number;
  countWithTime: number;
}

export interface ReadyMonthSeparatorBarProps {
  monthLabel: string;
  /** null — показать N/A (месяц свёрнут или данные ещё не загружены) */
  projectCount: number | null;
  /** null — показать N/A */
  timeFormatted: string | null;
  /** Видимые проекты в процессе: число строк, сумма time, сколько с валидным time (бейдж) */
  inProcess: ReadyMonthInProgressSummary & { projectCount: number };
  expanded: boolean;
  onToggle: () => void;
  isLoadingProjects?: boolean;
  showReadyTime?: boolean;
  showInProgressTime?: boolean;
  showProjectCount?: boolean;
  /** Блок «Проектов в процессе» слева (только у самого нового месяца — задаёт родитель) */
  showActiveProjectsCount?: boolean;
  onBarContextMenu?: (e: React.MouseEvent) => void;
  /** Разделитель «Архив»: только название и счётчик, без часов и блока «в процессе» */
  variant?: 'month' | 'archive';
}

/** Полоса разделителя (контент). Сама «прилипает» через родителя `.projects-table__month-sticky-pin`. */
export function ReadyMonthSeparatorBar({
  monthLabel,
  projectCount,
  timeFormatted,
  inProcess,
  expanded,
  onToggle,
  isLoadingProjects,
  showReadyTime = true,
  showInProgressTime = true,
  showProjectCount = true,
  showActiveProjectsCount = true,
  onBarContextMenu,
  variant = 'month',
}: ReadyMonthSeparatorBarProps) {
  const isArchive = variant === 'archive';
  const showLeft = !isArchive && (showActiveProjectsCount || showInProgressTime);
  const effectiveShowReadyTime = !isArchive && showReadyTime;
  const effectiveShowProjectCount = showProjectCount;

  return (
    <div className="projects-table__month-sep-bar">
      <div className="projects-table__month-sep-bleed">
        <div className="projects-table__month-sep-inner" onContextMenu={onBarContextMenu}>
          <div className="projects-table__month-sep-col projects-table__month-sep-col--left">
            {showLeft ? (
              <div
                className="projects-table__month-sep-inprogress projects-table__month-sep-inprogress--leading"
                aria-label="Проекты в процессе по таблице"
              >
                {showActiveProjectsCount ? (
                  <>
                    <span className="projects-table__month-sep-inprogress-label">Проектов в процессе:</span>
                    <span className="projects-table__month-sep-inprogress-label projects-table__month-sep-inprogress-time">
                      {inProcess.projectCount}
                    </span>
                  </>
                ) : null}
                {showInProgressTime ? (
                  <>
                    <span className="projects-table__month-sep-inprogress-label">часов:</span>
                    <span className="projects-table__month-sep-inprogress-label projects-table__month-sep-inprogress-time">
                      {formatSecondsToHMS(inProcess.totalSeconds)}
                    </span>
                    <span className="projects-table__month-sep-inprogress-badge">
                      {inProcess.countWithTime}
                    </span>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="projects-table__month-sep-col projects-table__month-sep-col--center">
            {monthLabel.toUpperCase()}
          </div>
          <div className="projects-table__month-sep-col projects-table__month-sep-col--right">
            {effectiveShowProjectCount ? (
              <>
                <span className="projects-table__month-sep-muted">
                  {isArchive ? 'В архиве:' : 'Готовых проектов:'}
                </span>
                <span className="projects-table__month-sep-strong">
                  {projectCount === null ? 'N/A' : projectCount}
                </span>
                {isLoadingProjects ? (
                  <span className="projects-table__month-sep-muted projects-table__month-sep-loading">…</span>
                ) : null}
              </>
            ) : null}
            {effectiveShowReadyTime ? (
              <>
                <span className="projects-table__month-sep-muted">часов:</span>
                <span className="projects-table__month-sep-strong">
                  {timeFormatted === null ? 'N/A' : timeFormatted}
                </span>
              </>
            ) : null}
            <button
              type="button"
              className="projects-table__month-sep-toggle"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={
                isArchive
                  ? expanded
                    ? 'Свернуть архив'
                    : 'Развернуть архив'
                  : expanded
                    ? 'Свернуть месяц'
                    : 'Развернуть месяц'
              }
            >
              {expanded ? <ExpandLessIcon size={28} /> : <ExpandMoreIcon size={28} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface ReadyMonthSeparatorSpacerProps {
  top: number;
  measureRef: (el: HTMLDivElement | null) => void;
  dataIndex: number;
}

/** Невидимая виртуальная строка той же высоты, что и разделитель — держит раскладку и measureElement. */
export function ReadyMonthSeparatorSpacer({ top, measureRef, dataIndex }: ReadyMonthSeparatorSpacerProps) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      measureRef(el);
    },
    [measureRef]
  );

  useLayoutEffect(() => {
    if (elRef.current) measureRef(elRef.current);
  }, [measureRef, top, dataIndex]);

  return (
    <div
      ref={combinedRef}
      data-index={dataIndex}
      className="projects-table__virtual-row projects-table__month-sep-spacer"
      style={{ top: `${top}px`, transform: 'none' }}
      aria-hidden
    />
  );
}
